'use client';

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Calendar, Car, ChevronRight, Grid3X3, Home, Radar, Smartphone, User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Suspense, use } from 'react';
import { ProtectedRoute } from '@/components/auth';
import { DefaultPageLayout } from '@/components/templates/DefaultPageLayout';
import { UserMetadataProvider } from '@/contexts/UserMetadataContext';
import { useAuth } from '@/hooks/useAuthQuery';
interface AppsLayoutProps {
  children: React.ReactNode;
  params: Promise<{
    locale: string;
  }>;
}

// App configuration with metadata
const apps = [{
  id: 'profile',
  titleKey: 'userProfile',
  descriptionKey: 'userProfileDescription',
  icon: User,
  href: '/apps/profile',
  color: 'text-green-500',
  bgColor: 'bg-green-50 dark:bg-green-900/20',
  category: 'personal'
}, {
  id: 'my-life-in-weeks',
  titleKey: 'myLifeInWeeks',
  descriptionKey: 'myLifeInWeeksDescription',
  icon: Calendar,
  href: '/apps/my-life-in-weeks',
  color: 'text-blue-500',
  bgColor: 'bg-blue-50 dark:bg-blue-900/20',
  category: 'productivity'
}, {
  id: 'car-dashboard',
  titleKey: 'carDashboard',
  descriptionKey: 'carDashboardDescription',
  icon: Car,
  href: '/apps/car-dashboard',
  color: 'text-red-500',
  bgColor: 'bg-red-50 dark:bg-red-900/20',
  category: 'automotive'
}, {
  id: 'technology-radar',
  titleKey: 'technologyRadar',
  descriptionKey: 'technologyRadarDescription',
  icon: Radar,
  href: '/apps/technology-radar',
  color: 'text-purple-500',
  bgColor: 'bg-purple-50 dark:bg-purple-900/20',
  category: 'productivity'
}];
function AppCard(t0) {
  const $ = _c(29);
  const {
    app,
    locale
  } = t0;
  const Icon = app.icon;
  const t = useTranslations("apps");
  const t1 = `/${locale}${app.href}`;
  const t2 = `p-3 rounded-lg ${app.bgColor} group-hover:scale-110 transition-transform duration-200`;
  const t3 = `w-6 h-6 ${app.color}`;
  let t4;
  if ($[0] !== Icon || $[1] !== t3) {
    t4 = <Icon className={t3} />;
    $[0] = Icon;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t2 || $[4] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[3] = t2;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== app.titleKey || $[7] !== t) {
    t6 = t(app.titleKey);
    $[6] = app.titleKey;
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{t6}</h3>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== app.descriptionKey || $[12] !== t) {
    t8 = t(app.descriptionKey);
    $[11] = app.descriptionKey;
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{t8}</p>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== app.category) {
    t10 = <div className="flex items-center text-xs text-gray-500 dark:text-gray-500 mt-2 capitalize"><span>{app.category}</span></div>;
    $[16] = app.category;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7 || $[20] !== t9) {
    t11 = <div className="flex-1 min-w-0">{t7}{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t7;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11 || $[24] !== t5) {
    t13 = <div className="flex items-start gap-4">{t5}{t11}{t12}</div>;
    $[23] = t11;
    $[24] = t5;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t1 || $[27] !== t13) {
    t14 = <Link href={t1} className="group block p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 hover:shadow-md">{t13}</Link>;
    $[26] = t1;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}
function AppsGrid(t0) {
  const $ = _c(25);
  const {
    locale
  } = t0;
  const t = useTranslations("apps");
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== locale || $[1] !== t) {
    const appsByCategory = apps.reduce(_temp, {} as Record<string, typeof apps>);
    t3 = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8";
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Grid3X3 className="w-8 h-8 text-blue-500" />;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t) {
      t6 = t("title");
      $[7] = t;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== t6) {
      t7 = <div className="flex items-center justify-center gap-2 mb-4">{t5}<h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t6}</h1></div>;
      $[9] = t6;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t) {
      t8 = t("description");
      $[11] = t;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== t8) {
      t9 = <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">{t8}</p>;
      $[13] = t8;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    if ($[15] !== t7 || $[16] !== t9) {
      t4 = <div className="text-center mb-12">{t7}{t9}</div>;
      $[15] = t7;
      $[16] = t9;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t1 = "space-y-12";
    t2 = Object.entries(appsByCategory).map(t10 => {
      const [category, categoryApps] = t10;
      return <div key={category}><h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 capitalize">{t(`categories.${category}`)}</h2><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{categoryApps.map(app_0 => <AppCard key={app_0.id} app={app_0} locale={locale} />)}</div></div>;
    });
    $[0] = locale;
    $[1] = t;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[18] !== t1 || $[19] !== t2) {
    t5 = <div className={t1}>{t2}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
}
function _temp(acc, app) {
  if (!acc[app.category]) {
    acc[app.category] = [];
  }
  acc[app.category].push(app);
  return acc;
}
export default function AppsLayout(t0) {
  const $ = _c(22);
  const {
    children,
    params
  } = t0;
  const {
    locale
  } = use(params);
  const pathname = usePathname();
  const isAppsIndexPage = pathname === `/${locale}/apps` || pathname === `/${locale}/apps/` || pathname === "/apps";
  const [user, loading, error] = useAuth();
  if (loading || error) {
    return null;
  }
  const t1 = `/${locale}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Home className="w-4 h-4" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <Link href={t1} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1 transition-colors">{t2}Home</Link>;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronRight className="w-4 h-4 text-gray-400" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = `/${locale}/apps`;
  let t6;
  if ($[4] !== t5) {
    t6 = <Link href={t5} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors">Apps</Link>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t3 || $[7] !== t6) {
    t7 = <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"><div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4"><nav className="flex items-center space-x-2 text-sm">{t3}{t4}{t6}</nav></div></div>;
    $[6] = t3;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== children || $[11] !== isAppsIndexPage || $[12] !== locale) {
    t9 = isAppsIndexPage ? <AppsGrid locale={locale} /> : children;
    $[10] = children;
    $[11] = isAppsIndexPage;
    $[12] = locale;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <main><Suspense fallback={t8}>{t9}</Suspense></main>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7) {
    t11 = <div className="min-h-screen bg-gray-50 dark:bg-gray-900">{t7}{t10}</div>;
    $[16] = t10;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== user) {
    t12 = <ProtectedRoute><DefaultPageLayout><UserMetadataProvider authUser={user}>{t11}</UserMetadataProvider></DefaultPageLayout></ProtectedRoute>;
    $[19] = t11;
    $[20] = user;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}